import { useCallback, useRef, useState, useMemo, useEffect, ChangeEvent } from 'react'
import { useDispatch } from 'react-redux'

import moment from 'moment'

import { setVideoProgress } from 'ducks/actions'

import { logToAnalytics, uploadPlaybookDone, batchUpload, uuid } from 'modules'

import { useAuth, useNotification } from 'hooks'

const ALLOWED_MIME_TYPES = ['video/mp4', 'video/quicktime', 'video/webm']
const ALLOWED_FORMATS = ['.mp4', '.mov', '.webm']

type Props = {
    onUploadCallback?: (playbookIds: Array<string>, batchUploadId?: string) => void
}

export const useUploader = ({ onUploadCallback }: Props) => {
    const dispatch = useDispatch()

    const startRecordingTime = useRef<number>(0)
    const recordingDuration = useRef<number>(0)
    const fileUploader = useRef<any>(null)

    const [isRecording, setIsRecording] = useState(false)
    const [filesToStore, setFilesToStore] = useState<Array<{
        name: string
        type?: string
        size?: number
    }> | null>(null)
    const [progress, setProgress] = useState(0)
    const [processedFiles, setProcessedFiles] = useState<string[]>([])
    const [processedFilesCounter, setProcessedFilesCounter] = useState(0)
    const [postProcessing, setPostProcessing] = useState(false)

    const { displayName, photoURL, email, uid, token } = useAuth()
    const { showErrorNotification, showWarningNotification } = useNotification()

    const uploadedBy = useMemo(
        () => ({ displayName, photoURL, email }),
        [displayName, email, photoURL]
    )

    const handleUploadError = (error: string) => {
        setProgress(0)
        setProcessedFilesCounter(prev => prev + 1)
        console.error(error)
    }

    const handleUploadSuccess = useCallback(
        async (
            eventName: string,
            playbookId: string,
            fileName: string,
            file: { name: any; type: any; size: number | undefined },
            url: string
        ) => {
            setPostProcessing(true)
            setProgress(0)
            const originalFileName = fileName || file?.name || filesToStore?.[0].name
            const mimeType = file?.type || filesToStore?.[0].type || ''
            const fileSize = file?.size || filesToStore?.[0].size

            const title = originalFileName
                .replace(/.mp4$/i, '')
                .replace(/.webm$/i, '')
                .replace(/.mov$/i, '')

            const ok = await uploadPlaybookDone({
                token,
                playbookId,
                originalFileName,
                uid,
                type: 'webapp',
                uploadedBy,
                isRecording,
                duration: recordingDuration.current,
                mimeType,
                fileSize,
                url
            })
            if (!ok) {
                handleUploadError('An error occured')
                logToAnalytics('failed_to_upload_after_recording', {
                    'pb-id': playbookId,
                    'pb-title': title
                })
                showErrorNotification(
                    'Something went wrong while uploading your video, please try again'
                )
            } else {
                if (eventName) {
                    logToAnalytics(eventName, {
                        'pb-id': playbookId,
                        'pb-title': title
                    })
                }
                dispatch(
                    setVideoProgress({
                        playbookId,
                        fileName: title,
                        source: 'playbooks'
                    })
                )

                setProcessedFiles(processedFiles => [...processedFiles, playbookId])
                setProcessedFilesCounter(prev => prev + 1)
            }
            setPostProcessing(false)
        },
        [dispatch, isRecording, showErrorNotification, token, uid, uploadedBy, filesToStore]
    )

    const customOnChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const {
            target: { files }
        } = event
        setIsRecording(false)
        setFilesToStore(Array.from(files || []))
    }

    const onStop = useCallback((blob: any) => {
        const date = moment().format('L LTS')
        recordingDuration.current = Date.now() - startRecordingTime.current
        setIsRecording(true)
        setFilesToStore([
            new File([blob], `Screen Recording ${date}.webm`, {
                type: 'video/webm'
            })
        ])
    }, [])

    const onStart = useCallback(() => (startRecordingTime.current = Date.now()), [])

    const startUploadManually = useCallback(() => {
        if (filesToStore) {
            filesToStore.forEach(file => {
                // temp until we support other files
                const isAllowed = ALLOWED_FORMATS.some(format => file.name.endsWith(format))
                if (!ALLOWED_MIME_TYPES.includes(file.type || '') || !isAllowed) {
                    showWarningNotification('We only accept .mp4, .mov and .webm file formats')
                    return null
                }
                fileUploader.current.startUpload(file)
            })
        }
    }, [filesToStore, showWarningNotification])

    useEffect(() => {
        if (!filesToStore || processedFilesCounter < filesToStore?.length) {
            return
        }

        const id = uuid()
        batchUpload({
            id,
            uploadedBy: uid,
            fileList: processedFiles,
            drive: 'local'
        })

        onUploadCallback?.(processedFiles, id)

        setFilesToStore(null)
        setProcessedFiles([])
        setProcessedFilesCounter(0)
    }, [processedFiles, uid, filesToStore, processedFilesCounter, onUploadCallback])

    return useMemo(
        () => ({
            uid,
            progress,
            setProgress,
            postProcessing,
            handleUploadError,
            handleUploadSuccess,
            onStart,
            onStop,
            startUploadManually,
            customOnChangeHandler,
            filesToStore,
            fileUploader
        }),
        [
            filesToStore,
            handleUploadSuccess,
            onStart,
            onStop,
            postProcessing,
            progress,
            startUploadManually,
            uid
        ]
    )
}
